/**
 * @fileoverview Hook powering the dedicated MongoDB dashboard — owns cluster
 *   list, drill-down selection, on-demand detail loading, and async state.
 * @module system-web/hooks/useMongoDbMonitoring
 *
 * Responsibilities:
 * - Load the cluster list on mount.
 * - Load per-cluster detail (serverStatus/databases/Atlas) when a cluster expands.
 * - Load per-database detail (dbStats/collections) when a database expands.
 * - Load per-collection detail (collStats/indexes) when a collection expands.
 * - Expose loading/error state and expand/collapse callbacks to the page.
 *
 * Dependencies:
 * - mongoDbMonitoringService (inherently-online monitoring API wrappers).
 *
 * Architecture:
 * - Local-first EXCEPTION (see mongoDbMonitoringService): monitoring data is
 *   live and non-persisted. The hook owns ALL async state + selection; the page
 *   renders from the hook and never calls the API directly.
 *
 * Notes:
 * - Deeper levels are fetched only when expanded, and cached until reloaded, so
 *   the dashboard never loads every database/collection eagerly.
 *
 * @hook useMongoDbMonitoring
 * Returns: cluster list + per-level async detail maps + expand/collapse + reload.
 * Side Effects: fetches the cluster list on mount; fetches detail on expand.
 * Offline Behavior: monitoring is inherently online — surfaces an error string
 *   when the network/API is unavailable (no cached fallback by design).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  listClusters,
  getClusterDetail,
  getDatabaseDetail,
  getCollectionDetail,
  type ClusterListItem,
  type ClusterDetail,
  type DatabaseDetail,
  type CollectionDetail,
} from '../services/mongoDbMonitoringService';

/**
 * @contract AsyncState
 * Purpose: Generic loading/error/data envelope for an on-demand fetch.
 * Fields:  data (T | null), loading, error (string | null).
 * Rules:   loading true only while an in-flight fetch is pending.
 * Sync:    n/a (live monitoring data).
 */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function dbKey(clusterId: string, dbName: string): string {
  return `${clusterId}::${dbName}`;
}

export function collKey(clusterId: string, dbName: string, collName: string): string {
  return `${clusterId}::${dbName}::${collName}`;
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function useMongoDbMonitoring() {
  const [clusters, setClusters] = useState<ClusterListItem[]>([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [clustersError, setClustersError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [clusterDetails, setClusterDetails] = useState<Record<string, AsyncState<ClusterDetail>>>({});
  const [databaseDetails, setDatabaseDetails] = useState<Record<string, AsyncState<DatabaseDetail>>>({});
  const [collectionDetails, setCollectionDetails] = useState<Record<string, AsyncState<CollectionDetail>>>({});

  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [expandedDatabases, setExpandedDatabases] = useState<Set<string>>(new Set());
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());

  const reloadClusters = useCallback(() => setReloadKey((k) => k + 1), []);

  // Load the cluster list on mount (and on reload).
  useEffect(() => {
    let cancelled = false;
    setLoadingClusters(true);
    setClustersError(null);
    listClusters()
      .then((res) => {
        if (!cancelled) setClusters(res.clusters);
      })
      .catch((err) => {
        if (!cancelled) setClustersError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingClusters(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const loadCluster = useCallback((clusterId: string, force = false) => {
    setClusterDetails((prev) => {
      if (!force && prev[clusterId]?.data) return prev;
      return { ...prev, [clusterId]: { data: prev[clusterId]?.data ?? null, loading: true, error: null } };
    });
    getClusterDetail(clusterId)
      .then((detail) => {
        setClusterDetails((prev) => ({ ...prev, [clusterId]: { data: detail, loading: false, error: null } }));
      })
      .catch((err) => {
        setClusterDetails((prev) => ({
          ...prev,
          [clusterId]: { data: prev[clusterId]?.data ?? null, loading: false, error: errMessage(err) },
        }));
      });
  }, []);

  const loadDatabase = useCallback((clusterId: string, dbName: string, force = false) => {
    const key = dbKey(clusterId, dbName);
    setDatabaseDetails((prev) => {
      if (!force && prev[key]?.data) return prev;
      return { ...prev, [key]: { data: prev[key]?.data ?? null, loading: true, error: null } };
    });
    getDatabaseDetail(clusterId, dbName)
      .then((detail) => {
        setDatabaseDetails((prev) => ({ ...prev, [key]: { data: detail, loading: false, error: null } }));
      })
      .catch((err) => {
        setDatabaseDetails((prev) => ({
          ...prev,
          [key]: { data: prev[key]?.data ?? null, loading: false, error: errMessage(err) },
        }));
      });
  }, []);

  const loadCollection = useCallback((clusterId: string, dbName: string, collName: string, force = false) => {
    const key = collKey(clusterId, dbName, collName);
    setCollectionDetails((prev) => {
      if (!force && prev[key]?.data) return prev;
      return { ...prev, [key]: { data: prev[key]?.data ?? null, loading: true, error: null } };
    });
    getCollectionDetail(clusterId, dbName, collName)
      .then((detail) => {
        setCollectionDetails((prev) => ({ ...prev, [key]: { data: detail, loading: false, error: null } }));
      })
      .catch((err) => {
        setCollectionDetails((prev) => ({
          ...prev,
          [key]: { data: prev[key]?.data ?? null, loading: false, error: errMessage(err) },
        }));
      });
  }, []);

  const toggleCluster = useCallback(
    (clusterId: string) => {
      // Decide first, then keep the updater pure so StrictMode double-invoke
      // never double-fires the API call.
      const willExpand = !expandedClusters.has(clusterId);
      setExpandedClusters((prev) => {
        const next = new Set(prev);
        if (next.has(clusterId)) next.delete(clusterId);
        else next.add(clusterId);
        return next;
      });
      if (willExpand) loadCluster(clusterId);
    },
    [loadCluster, expandedClusters],
  );

  const toggleDatabase = useCallback(
    (clusterId: string, dbName: string) => {
      const key = dbKey(clusterId, dbName);
      const willExpand = !expandedDatabases.has(key);
      setExpandedDatabases((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      if (willExpand) loadDatabase(clusterId, dbName);
    },
    [loadDatabase, expandedDatabases],
  );

  const toggleCollection = useCallback(
    (clusterId: string, dbName: string, collName: string) => {
      const key = collKey(clusterId, dbName, collName);
      const willExpand = !expandedCollections.has(key);
      setExpandedCollections((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      if (willExpand) loadCollection(clusterId, dbName, collName);
    },
    [loadCollection, expandedCollections],
  );

  const reloadCluster = useCallback((clusterId: string) => loadCluster(clusterId, true), [loadCluster]);

  return {
    clusters,
    loadingClusters,
    clustersError,
    reloadClusters,
    clusterDetails,
    databaseDetails,
    collectionDetails,
    expandedClusters,
    expandedDatabases,
    expandedCollections,
    toggleCluster,
    toggleDatabase,
    toggleCollection,
    reloadCluster,
  };
}
